<template>
  <div class="row f-row justify-content-center h-100">
    <!-- First Column -->
    <transition name="fadeCharts" v-if="!hideLeft">
      <div
        class="col text-center"
        v-bind:class="[expandLeft ? 'col-12' : 'col-5']"
      >
        <div class="h-100 d-flex flex-column">
          <!-- 2 Charts Column -->
          <div class="row f-row h-100">
            <div class="col col-12">
              <!-- Chart 1 -->
              <div class="h-50 d-flex flex-column" style="width: 100%;">
                <!-- Row: title -->
                <div class="row f-row">
                  <div class="markets-chart-title text-left d-flex">
                    <span class="mr-auto">
                      CIF ROTTERDAM HISTORICAL AND FORWARDS (USD/mt)
                    </span>
                    <div class="sidebarHidden">
                      <button
                        @click="expandLeftAnimation()"
                        class="btn mr-1"
                        style="width: 12px;"
                      >
                        <i class="fa fa-expand" v-if="!expandLeft"></i>
                        <i class="fa fa-compress" v-if="expandLeft"></i>
                      </button>
                    </div>
                  </div>
                </div>
                <!-- Row: controls and labels -->
                <div class="row f-row spot-row mt-1">
                  <div
                    class="col txt-label-primary-2"
                    v-bind:class="[expandLeft ? 'col-3' : 'col-auto']"
                  >
                    <i class="fa fa-minus ml-1" style="color:var(--expo)"></i>
                    <span class="inline-ul ml-1">
                      {{
                        interval === "day"
                          ? "DAILY"
                          : interval === "week"
                          ? "WEEKLY"
                          : "MONTHLY"
                      }}
                    </span>
                    <i
                      class="fa fa-minus ml-1"
                      style="color:var(--dark-fwd)"
                    ></i>
                    <span class="inline-ul ml-1">DAILY</span>
                    <div class="square-border-dark-fwd-slim ml-2"></div>
                    <span class="inline-ul ml-1">SWAPS</span>
                  </div>
                  <div class="col col-3" v-show="expandAnimation">
                    <HistoricalInterval
                      :showTitle="false"
                      :initPickedDateInterval="pickedIntervalHistory"
                      @eventOnChangeInterval="onHistoricalIntervalChange"
                    />
                  </div>
                  <div class="col col-3 ml-2" v-show="expandAnimation">
                    <RangeSlider
                      :labelTxt="'ZOOM SWAPS:'"
                      :dates="xRange.length > 1 ? xRange.slice(0, -1) : xRange"
                      :minIdx="zoomInitial"
                      :maxIdx="zoomFinal"
                      @eventOnMinRange="eventMinRange"
                      @eventOnMaxRange="eventMaxRange"
                    />
                  </div>
                </div>
                <!-- Row: Chart -->
                <CPOPastAndFwdsChart
                  :allData="allData"
                  :colorTheme="colorTheme"
                  :div1Name="'malaysia_hist-div'"
                  :div2Name="'malaysia_fwd-div'"
                  :fwdColor="darkFwd"
                  :fwdData="rotterdamFwdData"
                  :histData="rotterdamHistDataSliced"
                  :hoverName="'MAL'"
                  :interval="interval"
                  :isCandles="false"
                  :monthData="rotterdamHistDataMonth"
                  :processData="processData"
                  :progressPosition="22.5"
                  :renderChartsSignal="renderPriceChartSignal"
                  :showLoading="showLoadingAllCalcs"
                  :svg1Name="'svg1-malaysia_hist'"
                  :svg2Name="'svg2-malaysia_fwd'"
                />
              </div>

              <!-- Chart 2 -->
              <div class="h-50 d-flex flex-column" style="width: 100%;">
                <!-- Row: title -->
                <div class="row f-row">
                  <div class="markets-chart-title col col-12">
                    USD/COP HISTORICAL AND FORWARDS
                  </div>
                </div>
                <!-- Row: controls and labels -->
                <div class="row f-row mt-1">
                  <div class="col col-auto txt-label-primary-2">
                    <div class="triangle-topleft-analytics ml-1"></div>
                    <span class="inline-ul ml-1">
                      {{
                        interval === "day"
                          ? "DAILY"
                          : interval === "week"
                          ? "WEEKLY"
                          : "MONTHLY"
                      }}
                    </span>
                    <div class="triangle-topleft-fwd ml-2"></div>
                    <span class="inline-ul ml-1">DAILY</span>
                    <div class="square-border-fwd-slim ml-2"></div>
                    <span class="inline-ul ml-1">FORWARDS</span>
                  </div>
                </div>
                <!-- Row: Chart -->
                <CPOPastAndFwdsChart
                  :allData="allData"
                  :colorTheme="colorTheme"
                  :div1Name="'rate_hist-div'"
                  :div2Name="'rate_fwd-div'"
                  :fwdColor="colorFwd"
                  :fwdData="fwdUSDCOP"
                  :histData="histUSDCOPSliced"
                  :hoverName="'RATE'"
                  :interval="interval"
                  :isCandles="true"
                  :monthData="histUSDCOPMonth"
                  :processData="processData"
                  :progressPosition="72.5"
                  :renderChartsSignal="renderRatesChartSignal"
                  :showLoading="showLoadingAllCalcs"
                  :svg1Name="'svg1-rate_hist'"
                  :svg2Name="'svg2-rate_fwd'"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- ======================== //// ============================== -->
    <!-- Second Column -->
    <transition name="fadeCharts" v-if="!hideRight">
      <div
        class="col text-center results-divider"
        v-bind:class="[expandRight ? 'col-12' : '', 'col-7']"
      >
        <!-- Chart -->
        <div class="d-flex flex-column" style="height: calc(100% - 160px)">
          <!-- Row: title and resize button -->
          <div class="row f-row">
            <div class="markets-chart-title text-left d-flex">
              <span class="mr-auto">
                CIF ROTTERDAM IMPORTS / EXPORTS HISTORICAL AND SWAPS
              </span>
              <div class="sidebarHidden">
                <button
                  @click="expandRightAnimation()"
                  class="btn mr-1"
                  style="width: 12px;"
                >
                  <i class="fa fa-expand" v-if="!expandRight"></i>
                  <i class="fa fa-compress" v-if="expandRight"></i>
                </button>
              </div>
            </div>
          </div>
          <!-- Row: Historical time selector -->
          <div class="row f-row mt-1">
            <div
              class="col col-auto txt-label-primary-2"
              style="justify-content: left!important;"
            >
              <i class="fa fa-minus ml-1 mr-1" style="color:var(--expo)"></i>
              MONTHLY
              <i class="fa fa-minus ml-1" style="color:var(--hedge)"></i>
              <span class="inline-ul ml-1">DAILY</span>
              <div class="square-border-hedge-slim ml-2"></div>
              <span class="inline-ul ml-1">SWAPS</span>
            </div>
            <div class="col col-4" style="height:90%; width:100%;">
              <HistoricalInterval
                :showTitle="false"
                :initPickedDateInterval="pickedIntervalHistory"
                @eventOnChangeInterval="onHistoricalIntervalChange"
              />
            </div>
            <div class="col col-cx ml-2 mr-2" style="height:90%; width:100%;">
              <RangeSlider
                :labelTxt="'ZOOM:'"
                :dates="xRange.length > 1 ? xRange.slice(0, -1) : xRange"
                :minIdx="zoomInitial"
                :maxIdx="zoomFinal"
                @eventOnMinRange="eventMinRange"
                @eventOnMaxRange="eventMaxRange"
              />
            </div>
          </div>
          <!-- Row: Chart -->
          <CPOPastAndFwdsChart
            :showLoading="showLoadingAllCalcs"
            :colorTheme="colorTheme"
            :allData="allData"
            :div1Name="'fep_hist-div'"
            :div2Name="'fep_fwd-div'"
            :fwdColor="colorHedge(this.colorTheme)"
            :fwdData="allData"
            :histData="histFEP"
            :hoverName="'ROTT'"
            :interval="interval"
            :isCandles="false"
            :isFEP="false"
            :monthData="monthFEP"
            :processData="processData"
            :progressPosition="37"
            :renderChartsSignal="renderFEPSignal"
            :svg1Name="'svg1-fep_hist'"
            :svg2Name="'svg2-fep_fwd'"
          />
        </div>

        <!-- Table -->
        <div
          class="d-flex flex-column bdr-t"
          style="width: 100%; height: 160px"
        >
          <CPOTableFEP
            :freight="freight"
            :tariff="tariff"
            :aveDataFEP="aveDataFEP"
            :inDate="xRange[zoomInitial]"
            :endDate="xRange[zoomFinal]"
            :isFEP="false"
            @eventOnUserFreightChange="onUserFreightChange"
            @eventOnUserTariffChange="onUserTariffChange"
          />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import CPOPastAndFwdsChart from "@/components/markets/CPOPastAndFwdsChart";
import CPOTableFEP from "@/components/markets/CPOTableFEP";
import HistoricalInterval from "@/components/intervals/AnalyticsHistoricalInterval";
import RangeSlider from "@/components/analytics/RangeSlider";
import {
  colorHedge,
  colorFwd,
  darkFwd
} from "@/_core/_helpers/_parametric_tools";

export default {
  name: "FEP",
  components: {
    CPOPastAndFwdsChart,
    CPOTableFEP,
    HistoricalInterval,
    RangeSlider
  },
  props: {
    allData: { required: true, default: () => [] },
    aveDataFEP: { required: true, default: () => [] },
    colorTheme: { required: true, type: Boolean, default: true },
    expandLeft: { required: true },
    expandRight: { required: true },
    freight: { required: true },
    fwdUSDCOP: { required: true, default: () => [] },
    hideLeft: { required: true },
    hideRight: { required: true },
    histFEP: { required: true, default: () => [] },
    histUSDCOPMonth: { required: true, default: () => [] },
    histUSDCOPSliced: { required: true, default: () => [] },
    interval: { required: true },
    monthFEP: { required: true, default: () => [] },
    pickedIntervalHistory: { required: true },
    renderFEPSignal: { required: true, default: 0 },
    renderPriceChartSignal: { required: true, default: 0 },
    renderRatesChartSignal: { required: true, default: 0 },
    rotterdamFwdData: { required: true, default: () => [] },
    rotterdamHistDataMonth: { required: true, default: () => [] },
    rotterdamHistDataSliced: { required: true, default: () => [] },
    showLoadingAllCalcs: { required: true, type: Boolean, default: false },
    tariff: { required: true },
    xRange: { required: true },
    zoomFinal: { required: true },
    zoomInitial: { required: true },
    // ============ Data Classes ===============
    processData: { required: true }
  },
  data() {
    return {
      pickedIntervalHistoryAux: "",
      expandAnimation: false,
      // ============ Colors ============
      colorHedge: colorHedge,
      colorFwd: colorFwd,
      darkFwd: darkFwd
    };
  },
  watch: {
    expandLeft(val) {
      if (val) {
        setTimeout(() => {
          this.expandAnimation = val;
        }, 2000);
      } else {
        this.expandAnimation = val;
      }
    }
  },
  methods: {
    // === user interactions ===
    expandLeftAnimation() {
      this.$emit("eventOnExpandLeftAnimation");
    },
    expandRightAnimation() {
      this.$emit("eventOnExpandRightAnimation");
    },
    onUserSelectCPOCurr(val) {
      this.$emit("eventOnUserSelectCPOCurr", val);
    },
    onUserSelectCurr(val) {
      this.$emit("eventOnUserSelectCurr", val);
    },
    onHistoricalIntervalChange(val) {
      this.$emit("eventOnChangeInterval", val);
    },
    eventMinRange(val) {
      this.$emit("eventOnMinRange", val);
    },
    eventMaxRange(val) {
      this.$emit("eventOnMaxRange", val);
    },
    onUserFreightChange(val) {
      this.$emit("eventOnUserFreightChange", val);
    },
    onUserTariffChange(val) {
      this.$emit("eventOnUserTariffChange", val);
    }
  }
};
</script>

<style scoped></style>
